import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { MJCommunicationProviderEntity } from '@memberjunction/core-entities';
import { Subject } from 'rxjs';
import { BCSaaSChannelTypeEntity } from '@bluecypress/bcsaas-entities';
import { IzzyChannelTypeExtensionEntity } from 'mj_generatedentities';
import { CommunicationEngineBase } from '@memberjunction/communication-types';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

interface ChannelTypeDisplay {
  channelType: BCSaaSChannelTypeEntity;
  provider: MJCommunicationProviderEntity | null;
  isEditing: boolean;
  editForm: ChannelTypeEditForm;
}

interface ChannelTypeEditForm {
  name: string;
  description: string;
  communicationProviderID: string;
  actionInheritMode: 'Organization' | 'ChannelType' | 'Combined';
  status: 'Active' | 'Pending' | 'Revoked';
}

@Component({
  standalone: false,
  selector: 'izzy-channel-types',
  templateUrl: './channel-types.component.html',
  styleUrls: ['./channel-types.component.css']
})
export class ChannelTypesComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor() {}

  protected channelTypes: BCSaaSChannelTypeEntity[] = [];
  protected channelTypeDisplays: ChannelTypeDisplay[] = [];
  protected providers: MJCommunicationProviderEntity[] = [];
  // Post-BAC: ActionInheritMode moved off BCSaaS.ChannelType into Izzy.ChannelTypeExtension (1:1 by ChannelTypeID).
  protected channelTypeExtensions: IzzyChannelTypeExtensionEntity[] = [];

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';

  protected isCreating: boolean = false;
  protected createForm: ChannelTypeEditForm = this.getEmptyForm();

  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getEmptyForm(): ChannelTypeEditForm {
    return {
      name: '',
      description: '',
      communicationProviderID: '',
      actionInheritMode: 'Organization',
      status: 'Active'
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Load providers from Communication Engine - usually alrady cached
      await CommunicationEngineBase.Instance.Config(false);
      this.providers = CommunicationEngineBase.Instance.Providers;

      // Load channel types
      const rv = new RunView();
      const result = await rv.RunView<BCSaaSChannelTypeEntity>({
        EntityName: 'BC: Channel Types',
        OrderBy: 'Name ASC',
        ResultType: 'entity_object'
      });
      this.channelTypes = result.Results;

      // Load Izzy.ChannelTypeExtension rows (1:1 by ChannelTypeID)
      if (this.channelTypes.length > 0) {
        const ids = this.channelTypes.map(ct => `'${ct.ID}'`).join(',');
        const rvExt = new RunView();
        const extResult = await rvExt.RunView<IzzyChannelTypeExtensionEntity>({
          EntityName: 'Channel Type Extensions',
          ExtraFilter: `ChannelTypeID IN (${ids})`,
          ResultType: 'entity_object'
        });
        this.channelTypeExtensions = extResult.Success ? extResult.Results : [];
      } else {
        this.channelTypeExtensions = [];
      }

      this.channelTypeDisplays = this.channelTypes.map(ct => this.createDisplay(ct));

      // Emit default status (channel types don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading channel types:', error);
      this.errorMessage = 'Failed to load channel types.';
    } finally {
      this.isLoading = false;
    }
  }

  private createDisplay(channelType: BCSaaSChannelTypeEntity): ChannelTypeDisplay {
    const provider = this.providers.find(p => p.ID === channelType.CommunicationProviderID) || null;
    return {
      channelType,
      provider,
      isEditing: false,
      editForm: this.channelTypeToForm(channelType)
    };
  }

  /** Sync lookup for the channel-type's extension row (post-BAC). */
  private getExtensionForChannelType(channelTypeId: string): IzzyChannelTypeExtensionEntity | null {
    return this.channelTypeExtensions.find(e => e.ChannelTypeID === channelTypeId) || null;
  }

  private channelTypeToForm(ct: BCSaaSChannelTypeEntity): ChannelTypeEditForm {
    const ext = this.getExtensionForChannelType(ct.ID);
    return {
      name: ct.Name,
      description: ct.Description || '',
      communicationProviderID: ct.CommunicationProviderID || '',
      actionInheritMode: (ext?.ActionInheritMode as 'Organization' | 'ChannelType' | 'Combined') || 'Organization',
      status: ct.Status as 'Active' | 'Pending' | 'Revoked'
    };
  }

  /**
   * Save (or create) the Izzy.ChannelTypeExtension row with the form's ActionInheritMode.
   */
  private async saveExtensionForChannelType(
    channelTypeId: string,
    actionInheritMode: 'Organization' | 'ChannelType' | 'Combined'
  ): Promise<boolean> {
    const md = new Metadata();
    let ext = this.getExtensionForChannelType(channelTypeId);
    let isNew = false;
    if (!ext) {
      ext = await md.GetEntityObject<IzzyChannelTypeExtensionEntity>('Channel Type Extensions');
      ext.ChannelTypeID = channelTypeId;
      isNew = true;
    }
    ext.ActionInheritMode = actionInheritMode;
    const ok = await ext.Save();
    if (!ok) {
      console.error('Failed to save ChannelTypeExtension:', ext.LatestResult);
      return false;
    }
    if (isNew) {
      this.channelTypeExtensions.push(ext);
    }
    return true;
  }

  get filteredChannelTypes(): ChannelTypeDisplay[] {
    return this.channelTypeDisplays.filter(ctd => {
      const matchesSearch = !this.searchQuery.trim() ||
        ctd.channelType.Name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (ctd.channelType.Description && ctd.channelType.Description.toLowerCase().includes(this.searchQuery.toLowerCase()));
      return matchesSearch;
    });
  }

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.name.trim()) {
      this.errorMessage = 'Channel type name is required.';
      return;
    }

    this.isSaving = true;
    try {
      const md = new Metadata();
      // Post-BAC, creating a channel type writes TWO rows: __BCSaaS.ChannelType (base) and
      // Izzy.ChannelTypeExtension (1:1 child, FK'd to the base). They must commit atomically —
      // a back-to-back Save() of each races and the extension insert can fail
      // FK_ChannelTypeExtension_ChannelType. A TransactionGroup defers both and submits as one.
      const tg = await md.CreateTransactionGroup();

      const newCT = await md.GetEntityObject<BCSaaSChannelTypeEntity>('BC: Channel Types');
      newCT.NewRecord();
      newCT.Name = this.createForm.name.trim();
      newCT.Description = this.createForm.description.trim() || null;
      newCT.CommunicationProviderID = this.createForm.communicationProviderID || null;
      newCT.Status = this.createForm.status;
      newCT.TransactionGroup = tg;
      await newCT.Save();

      const ext = await md.GetEntityObject<IzzyChannelTypeExtensionEntity>('Channel Type Extensions');
      ext.NewRecord();
      ext.ChannelTypeID = newCT.ID;
      ext.ActionInheritMode = this.createForm.actionInheritMode;
      ext.TransactionGroup = tg;
      await ext.Save();

      if (!await tg.Submit()) {
        console.error('Failed to create channel type:', newCT.LatestResult, ext.LatestResult);
        this.errorMessage = newCT.LatestResult?.CompleteMessage || 'Failed to create channel type.';
        return;
      }

      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Channel type created');
    } catch (error) {
      console.error('Error creating channel type:', error);
      this.errorMessage = 'Failed to create channel type.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: ChannelTypeDisplay): void {
    display.isEditing = true;
    display.editForm = this.channelTypeToForm(display.channelType);
  }

  cancelEdit(display: ChannelTypeDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: ChannelTypeDisplay): Promise<void> {
    this.isSaving = true;
    try {
      display.channelType.Name = display.editForm.name.trim();
      display.channelType.Description = display.editForm.description.trim() || null;
      display.channelType.CommunicationProviderID = display.editForm.communicationProviderID || null;
      display.channelType.Status = display.editForm.status;

      const ok = await display.channelType.Save();
      if (!ok) {
        this.errorMessage = display.channelType.LatestResult?.CompleteMessage || 'Failed to update channel type.';
        return;
      }

      // Save the ChannelTypeExtension row (post-BAC: ActionInheritMode lives here)
      const extOk = await this.saveExtensionForChannelType(display.channelType.ID, display.editForm.actionInheritMode);
      if (!extOk) {
        this.errorMessage = 'Channel type updated but extension save failed; ActionInheritMode may not be saved.';
      }

      display.provider = this.providers.find(p => p.ID === display.channelType.CommunicationProviderID) || null;
      display.isEditing = false;
      this.showSuccess('Channel type updated');
    } catch (error) {
      console.error('Error updating channel type:', error);
      this.errorMessage = 'Failed to update channel type.';
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(display: ChannelTypeDisplay): void {
    this.showConfirmDialog(
      'Delete Channel Type',
      `Are you sure you want to delete "${display.channelType.Name}"? This may affect channels using this type.`,
      async () => {
        this.isSaving = true;
        try {
          await display.channelType.Delete();
          await this.loadData();
          this.showSuccess('Channel type deleted');
        } catch (error) {
          console.error('Error deleting channel type:', error);
          this.errorMessage = 'Failed to delete channel type.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Pending': return 'status-pending';
      case 'Revoked': return 'status-revoked';
      default: return '';
    }
  }

  getInheritModeLabel(mode: string): string {
    switch (mode) {
      case 'Organization': return 'Inherit from Organization';
      case 'ChannelType': return 'Channel Type Only';
      case 'Combined': return 'Combined (Org + Type)';
      default: return mode;
    }
  }

  getProviderIcon(providerName: string): string {
    const name = (providerName || '').toLowerCase();
    if (name.includes('email') || name.includes('graph') || name.includes('outlook')) return 'fa-envelope';
    if (name.includes('slack')) return 'fa-slack';
    if (name.includes('teams')) return 'fa-microsoft';
    if (name.includes('sms') || name.includes('twilio')) return 'fa-sms';
    if (name.includes('whatsapp')) return 'fa-whatsapp';
    return 'fa-plug';
  }

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadChannelTypesComponent(): void {}
